import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper
} from "@mui/material";
import imgUrl from "../assets/images/imgUrl";
import { apiServices } from "../services/apiServices"; 

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    country: '',
    mobile: '',
    designation: '',
    email: '',
    subject: '',
    message: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [errors, setErrors] = useState({});
  
  // Countries state
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Fetch countries logic (remains same)
  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await apiServices.getCountries();
      if (response.data.countries && Array.isArray(response.data.countries)) {
        setCountries(response.data.countries);
      } else {
        setCountries([
          { ids: 1, countryname: "USA" },
          { ids: 2, countryname: "India" },
          { ids: 3, countryname: "UK" }
        ]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  };

  useEffect(() => { fetchCountries(); }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const response = await apiServices.submitContactForm(formData);
      if (response.status === 200 || response.status === 202) {
        setAlert({ show: true, type: 'success', message: 'Contact form submitted successfully!' });
        setFormData({ firstName: '', lastName: '', country: '', mobile: '', designation: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      setAlert({ show: true, type: 'error', message: 'Failed to submit.' });
    } finally { setLoading(false); }
  };

  // Modern Input Styling Helper
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#FFFFFF", // White inputs pop against the mint background
      borderRadius: "8px",
      transition: "all 0.3s ease",
      "&:hover fieldset": { borderColor: "#006389" },
      "&.Mui-focused fieldset": { borderColor: "#399847" },
    },
    "& .MuiInputBase-input::placeholder": {
        color: "#666",
        opacity: 1
    }
  };

  return (
    <>
      {/* 1. HERO SECTION (Dimensions and code kept exactly as requested) */}
      <Box
        className="contactSectionBg commonBannerBg"
        sx={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
          px: 2,
        }}
        mb={5}
      >
        <Box
          sx={{
            position: "absolute",
            top: 80,
            left: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={imgUrl.doveImg}
            alt="Peace Dove Logo"
            sx={{ maxWidth: 200, height: "auto", mr: 1 }}
          />
        </Box>
      </Box>

      {/* 2. CONTACT BUTTON (Style preserved) */}
      <Box className="contactButton d-flex-align-center-justify-center">
        <Grid container spacing={0}>
          <Grid size={12}>
            <a href="mailto:peace@i-am-peacekeeper.com">
              <Button variant="contained" className="blueButton">
                peace@i-am-peacekeeper.com
              </Button>
            </a>
          </Grid>
        </Grid>
      </Box>

      {/* 3. HEADING (Modernized Typography) */}
      <Box className="d-flex-align-center-justify-center" mt={6} mb={2}>
        <Grid container direction="column" alignItems="center">
          <Grid size={12}>
            <Typography variant="h2" sx={{ 
                fontWeight: 700, 
                color: "#006389",
                letterSpacing: "-1px"
            }}>
              Contact Form
            </Typography>
            <Box sx={{ width: '50px', height: '4px', bgcolor: '#399847', mx: 'auto', mt: 1, borderRadius: '10px' }} />
          </Grid>
        </Grid>
      </Box>

      {/* 4. MODERNIZED CONTACT FORM BOX */}
      <Paper
        elevation={10} // Adds depth
        sx={{
          backgroundColor: "#D9E8E4",
          p: { xs: 3, md: 5 },
          maxWidth: 700, // Slightly wider for better field balance
          margin: "0 auto",
          borderRadius: 4, // More rounded corners
          mb: 10,
          border: '1px solid rgba(255,255,255,0.4)' // Glass-like edge
        }}
      >
        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setAlert({ ...alert, show: false })}
          >
            {alert.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth placeholder="First Name" variant="outlined" 
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  sx={inputSx}
                />
            </Grid>
             <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth placeholder="Last Name" variant="outlined" 
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  sx={inputSx}
                />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.country} sx={inputSx}>
                <Select 
                  value={formData.country} 
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  displayEmpty
                >
                    <MenuItem value="" disabled>Select Country</MenuItem>
                    {countries.map((country, i) => (
                    <MenuItem key={i} value={country.countryname}>{country.countryname}</MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth placeholder="Mobile (optional)" variant="outlined" 
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  sx={inputSx}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth placeholder="Designation" variant="outlined" 
                  value={formData.designation}
                  onChange={(e) => handleInputChange('designation', e.target.value)}
                  sx={inputSx}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField 
                  fullWidth placeholder="Email Address" variant="outlined" type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  sx={inputSx}
                />
            </Grid>

            <Grid item xs={12}>
                <TextField 
                  fullWidth placeholder="Subject" variant="outlined" 
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  sx={inputSx}
                />
            </Grid>

            <Grid item xs={12}>
                <TextField 
                  fullWidth placeholder="Message" multiline rows={4} variant="outlined" 
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  error={!!errors.message}
                  sx={inputSx}
                />
            </Grid>

            <Grid item xs={12} textAlign="center">
                <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                    background: "linear-gradient(to right, #006389, #399847)",
                    color: "#fff",
                    borderRadius:"50px",
                    fontWeight:"bold",
                    px: 6,
                    py: 1.5,
                    textTransform:"uppercase",
                    fontSize:"18px",
                    boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                    transition: "transform 0.2s ease",
                    "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                    }
                }}
                >
                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'SUBMIT'}
                </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </>
  );
};

export default Contact;